'use client';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { ChildContainerProps } from 'src/types';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import { Toast } from 'primereact/toast';
import { set } from 'date-fns';
import { requestToLoginAsPartner } from '../services/ClientService';
import { environment } from 'src/environments/environment';

interface AuthContextType {
  isLoggedIn: boolean;
  login: (
    email: string,
    password: string,
    captchaToken: string
  ) => Promise<boolean>;
  magicLogin: (token: string) => Promise<boolean>;
  logout: () => void;
  logoutPartner: () => void;
  switchUser: (response: any) => void;
  isAuthenticated: () => boolean;
  isTokenExpired: () => boolean;
  isRefreshTokenExpired: () => boolean;
  getAuthToken: () => string | undefined;
  getRefreshToken: () => string | undefined;
  getUserRole: () => string | undefined;
  getUserRegion: () => string | undefined;
  getUserIca: () => string | undefined;

  getUserName: () => string | undefined;
  getUserFullName: () => string | undefined;

  userRole: string | null;
  userRegion: string | null;
  isPartnerLoggedIn: boolean;
}

interface LoginResponse {
  message: string;
  data: {
    token: string;
    type: string;
    'refresh-token': string;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = ({ children }: ChildContainerProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPartnerLoggedIn, setIsPartnerLoggedIn] = useState(false);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userRegion, setUserRegion] = useState<string | null>(null);
  const [userIca, setUserIca] = useState<string | null>(null);

  const [userName, setUserName] = useState<string | null>(null);
  const [userFullName, setUserFullName] = useState<string | null>(null);

  const location = useLocation();
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const publicRoutes = [
    '/landing',
    '/notfound',
    '/auth/login',
    '/auth/access',
    '/auth/error',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/blog/',
  ];

  const login = async (email: string, password: string): Promise<boolean> => {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);

    try {
      const response = await axios.post<LoginResponse>(
        environment.REACT_APP_CLIENT_BASE_URL + '/api/client/login',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const authToken = response.data.data.token;
      const refreshToken = response.data.data['refresh-token'];

      Cookies.set('authToken', authToken, {
        secure: true, // HTTPS only
        expires: 7,
      }); // Store token in cookies for 7 days
      Cookies.set('refreshToken', refreshToken, {
        secure: true, // HTTPS only
        expires: 7,
      }); // Store token in cookies for 7 days

      const decoded: any = jwtDecode(authToken);
      const decodedRefreshToken: any = jwtDecode(refreshToken);

      setUserRole(decoded.user_role);
      setIsLoggedIn(true);
      setIsPartnerLoggedIn(false);
      navigate('/'); // Redirect to home page after successful login
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const magicLogin = async (token: string): Promise<boolean> => {
    const username = 'myissuerbenefit@flexiroam.com';
    const password = 'mnm#&S0b<U(Mc{1$469@';
    const formData = new FormData();
    formData.append('token', token);
    // formData.append('email', username);
    // formData.append('password', password);
    const loginUrl = environment.REACT_APP_CLIENT_BASE_URL + '/api/client/login';
    const magicLoginUrl = environment.REACT_APP_CLIENT_BASE_URL + '/api/client/magic/login';

    try {
      const response = await axios.post<LoginResponse>(
        magicLoginUrl,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const authToken = response.data.data.token;
      const refreshToken = response.data.data['refresh-token'];

      Cookies.set('authToken', authToken, {
        secure: true, // HTTPS only
        expires: 7,
      }); // Store token in cookies for 7 days
      Cookies.set('refreshToken', refreshToken, {
        secure: true, // HTTPS only
        expires: 7,
      }); // Store token in cookies for 7 days

      const decoded: any = jwtDecode(authToken);
      const decodedRefreshToken: any = jwtDecode(refreshToken);

      setUserRole(decoded.user_role);
      setIsLoggedIn(true);
      setIsPartnerLoggedIn(false);
      navigate('/'); // Redirect to home page after successful login
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = async () => {
    Cookies.remove('authToken');
    Cookies.remove('refreshToken');
    Cookies.remove('actualAuthToken');
    Cookies.remove('actualRefreshToken');
    setIsLoggedIn(false);
    setIsPartnerLoggedIn(false);
    setUserRole('');
    logoutPartner();
    // navigate('/auth/login');
  };

  const logoutPartner = async () => {
    const actualAuthToken = Cookies.get('actualAuthToken');
    const actualRefreshToken = Cookies.get('actualRefreshToken');

    if (actualAuthToken) {
      Cookies.set('authToken', actualAuthToken, { secure: true, expires: 7 });
    }
    if (actualRefreshToken) {
      Cookies.set('refreshToken', actualRefreshToken, {
        secure: true,
        expires: 7,
      });
    }

    Cookies.remove('actualAuthToken');
    Cookies.remove('actualRefreshToken');

    setIsPartnerLoggedIn(false);
    setUserRole(getUserRole()); // TODO: set user role as existing user
    setUserRegion(getUserRegion()); // TODO: set user role as existing user
    setUserIca(getUserIca()); // TODO: set user role as existing user

    // navigate('/user-management/users');
  };

  const switchUser = async (client_id: number) => {
    setLoading(true);
    try {
      const response = await requestToLoginAsPartner(client_id);
      if (response.response?.status === 422) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.response.data.message,
          life: 1000,
        });
      } else {
        const actualAuthToken = getAuthToken();
        const actualRefreshToken = getRefreshToken();

        if (actualAuthToken) {
          Cookies.set('actualAuthToken', actualAuthToken, {
            secure: true,
            expires: 7,
          });
        }
        if (actualRefreshToken) {
          Cookies.set('actualRefreshToken', actualRefreshToken, {
            secure: true,
            expires: 7,
          });
        }

        const partnerAuthToken = response?.data?.token;
        const partnerRefreshToken = response?.data['refresh-token'];

        if (partnerAuthToken && partnerRefreshToken) {
          Cookies.set('authToken', partnerAuthToken, {
            secure: true,
            expires: 7,
          });
          Cookies.set('refreshToken', partnerRefreshToken, {
            secure: true,
            expires: 7,
          });

          setIsPartnerLoggedIn(true); // Mark as partner login
          setIsLoggedIn(true);
          setUserRole(getUserRole());
          setUserRegion(getUserRegion());
          setUserName(getUserName());
          setUserFullName(getUserFullName());
          setUserIca(getUserIca());

          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: response.message,
            life: 1000,
          });
          setTimeout(() => {
            navigate('/');
          });
        } else {
          console.error('Switch user failed: Missing tokens');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isAuthenticated = (): boolean => {
    const token = Cookies.get('authToken');
    return !!token;
  };

  const isTokenExpired = (): boolean => {
    const authToken = getAuthToken();
    let tokenExpired = false;

    if (authToken) {
      const decoded: any = jwtDecode(authToken);

      if (decoded.exp > Date.now() / 1000) {
        tokenExpired = false;
      } else {
        tokenExpired = true;
      }
    }
    return tokenExpired;
  };

  const isRefreshTokenExpired = (): boolean => {
    const refreshToken = getRefreshToken();
    let tokenExpired = false;

    if (refreshToken) {
      const decoded: any = jwtDecode(refreshToken);

      if (decoded.exp > Date.now() / 1000) {
        tokenExpired = false;
      } else {
        tokenExpired = true;
      }
    }
    return tokenExpired;
  };

  const getAuthToken = (): string | undefined => {
    return Cookies.get('authToken');
  };

  const getRefreshToken = (): string | undefined => {
    return Cookies.get('refreshToken');
  };

  const getUserRole = () => {
    const token = Cookies.get('authToken');
    let userRole = '';
    if (token) {
      const decoded: any = jwtDecode(token);
      setUserRole(decoded.user_role);
      userRole = decoded.user_role;
    }
    return userRole;
  };

  const getUserRegion = () => {
    const token = Cookies.get('authToken');
    let userRegion = '';
    if (token) {
      const decoded: any = jwtDecode(token);
      setUserRegion(decoded.region);
      userRegion = decoded.region;
    }
    return userRegion;
  };

  const getUserIca = () => {
    const token = Cookies.get('authToken');
    let userIca = '';

    if (token) {
      const decoded: any = jwtDecode(token);
      setUserIca(decoded.ica);
      userIca = decoded.ica;
    }

    return userIca;
  };

  const getUserName = () => {
    const token = Cookies.get('authToken');
    let userName = '';
    if (token) {
      const decoded: any = jwtDecode(token);
      setUserName(decoded.first_name);
      userName = decoded.first_name;
    }
    return userName;
  };

  const getUserFullName = () => {
    const token = Cookies.get('authToken');
    let userFullName = '';

    if (token) {
      const decoded: any = jwtDecode(token);
      setUserFullName(decoded.first_name + ' ' + decoded.last_name);
      userFullName = decoded.first_name + ' ' + decoded.last_name;
    }
    return userFullName;
  };

  const isPublicPage = publicRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  const initializeAuth = async () => {
    const authToken = Cookies.get('authToken');
    const refreshToken = Cookies.get('refreshToken');
    const actualAuthToken = Cookies.get('actualAuthToken');

    if (
      authToken &&
      refreshToken &&
      !isRefreshTokenExpired() &&
      !isTokenExpired()
    ) {
      setIsLoggedIn(true);
      setIsPartnerLoggedIn(!!actualAuthToken);
      setUserRole(getUserRole());
      setUserRegion(getUserRegion());
      setUserName(getUserName());
      setUserFullName(getUserFullName());
      setUserIca(getUserIca());
    } else {
      console.log('Session expired. Logging out.');
      logout();
    }
  };

  useEffect(() => {
    if (!isPublicPage) {
      initializeAuth();
    }
  }, [isPublicPage]);

  return (
    <>
      <Toast ref={toast} />
      <AuthContext.Provider
        value={{
          isLoggedIn,
          isPartnerLoggedIn,
          login,
          magicLogin,
          logout,
          logoutPartner,
          switchUser,
          isAuthenticated,
          isTokenExpired,
          isRefreshTokenExpired,
          getAuthToken,
          getRefreshToken,
          getUserRole,
          getUserRegion,
          userRole,
          userRegion,
          getUserName,
          getUserFullName,
          getUserIca
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
export default AuthProvider;
